import * as React from 'react';

import { Badge, MultiSelectFilter } from '@rematter/ui-web';
import { type WritableAtom, useAtom, useAtomValue, useSetAtom } from 'jotai';

import { type FacilityRecord } from '../../../_graphql/queryFacilities';
import {
  $facilityFilterSearchTerm,
  $remoteFacilityFilterOptions,
} from '../../../_state/transactions.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { FilterOptionLabel } from './FilterOptionLabel';

interface FacilityColumnFilterProps {
  stateAtom: WritableAtom<Set<string>, [Set<string>], void>;
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ FACILITY COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const FacilityColumnFilter: React.FC<FacilityColumnFilterProps> = ({
  stateAtom,
  onClose,
}) => {
  const t = useTranslations();

  // Atoms
  const [selectedFacilityIdsSet, setSelectedFacilityIdsSet] =
    useAtom(stateAtom);
  const selectedFacilityIds = Array.from(selectedFacilityIdsSet);
  const {
    data: facilitiesData,
    isFetching: isFetchingFacilities,
    isLoading: isLoadingFacilities,
  } = useAtomValue($remoteFacilityFilterOptions);
  const setSearchTerm = useSetAtom($facilityFilterSearchTerm);

  // Memoized values
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedFacilityIds),
    [selectedFacilityIds],
  );

  const facilities = facilitiesData?.facilities ?? [];
  const selectedFacilities = facilities.filter((facility) =>
    selectedFacilityIds.includes(facility.id),
  );

  // Callbacks
  const handleSearch = React.useCallback(
    (query: string) => {
      setSearchTerm(query || undefined);
    },
    [setSearchTerm],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedFacilityIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedFacilityIdsSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedFacilityIdsSet(new Set());
  }, [setSelectedFacilityIdsSet]);

  const getLabel = React.useCallback(
    (item: FacilityRecord, isSelected: boolean) => (
      <FilterOptionLabel
        label={item.facilityName}
        badge={
          item.address && (
            <Badge color="neutral">
              {item.address.city && `${item.address.city},`}{' '}
              {item.address.state}
            </Badge>
          )
        }
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@FacilityColumnFilter min-w-[350px]"
      entries={facilities}
      selectedEntries={selectedFacilities}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(facility) => facility.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.transactionsView.filters.facility.searchPlaceholder}
      isLoading={isLoadingFacilities}
      isFetching={isFetchingFacilities}
    />
  );
};
